import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  bankImport,
  type ImportApplyResult,
  type ImportRowApply,
  type StatementPreview,
  type StatementRow,
} from '@/api/bankImport';
import { contractors as contractorsApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RECEIPT_OPS = ['FROM_BUYER', 'FROM_BANK', 'OTHER', 'LOAN'];
const PAYMENT_OPS = ['TO_SUPPLIER', 'TO_BANK', 'TAX', 'SALARY', 'LOAN_RETURN', 'OTHER'];

interface RowState extends ImportRowApply {
  parse_error: string | null;
  match_confidence: string;
  counterparty_name: string | null;
  duplicate_of_id: string | null;
  duplicate_of_kind: 'receipt' | 'payment' | null;
  duplicate_of_number: string | null;
}

export default function BankImportPage() {
  // UX β: org dropdown через usePermissionsByOrg, окремий orgs.list не потрібен
  // (availableOrgs повертає лише ті org де є bank.payment.manage)
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('bank.payment.manage');

  const [orgId, setOrgId] = useState('');
  const [bankId, setBankId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState<'canonical' | 'privatbank'>('canonical');

  const [preview, setPreview] = useState<StatementPreview | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [applyResult, setApplyResult] = useState<ImportApplyResult | null>(null);

  if (!orgId && availableOrgs[0]) setOrgId(availableOrgs[0].id);

  const banksQ = useQuery({
    queryKey: ['bank-accounts', orgId],
    queryFn: () => refs.bankAccounts(orgId),
    enabled: !!orgId,
  });
  if (!bankId && banksQ.data?.[0]) setBankId(banksQ.data[0].id);

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const contractorsList = useMemo(() => contractorsQ.data?.items ?? [], [contractorsQ.data]);

  const previewMut = useMutation({
    mutationFn: () => bankImport.preview(orgId, bankId, file!, format),
    onSuccess: (p) => {
      setPreview(p);
      setApplyResult(null);
      // Initial state for editing
      setRows(p.rows.map((r) => statementRowToState(r)));
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? String(e);
      alert(`Помилка: ${msg}`);
    },
  });

  const applyMut = useMutation({
    mutationFn: () => bankImport.apply(
      orgId, bankId,
      rows.map(({
        parse_error, match_confidence, counterparty_name,
        duplicate_of_id, duplicate_of_kind, duplicate_of_number,
        ...r
      }) => {
        void parse_error; void match_confidence; void counterparty_name;
        void duplicate_of_id; void duplicate_of_kind; void duplicate_of_number;
        return r;
      }),
    ),
    onSuccess: setApplyResult,
  });

  function updateRow(idx: number, patch: Partial<RowState>) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Імпорт банківської виписки</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1.5fr 1.5fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setBankId(''); setPreview(null); }}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють імпорт)
            </small>
          )}
          {!hasPermissionInAnyOrg && (
            <small style={{ color: '#b45309', fontSize: '0.75rem' }}>
              ⚠ У вас немає прав імпортувати виписки у жодній організації
            </small>
          )}
        </div>
        <div className="field">
          <label>Розрахунковий рахунок</label>
          <select value={bankId} onChange={(e) => { setBankId(e.target.value); setPreview(null); }}>
            <option value="">—</option>
            {banksQ.data?.map((b) => (
              <option key={b.id} value={b.id}>{b.account_name ?? b.iban}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Формат виписки</label>
          <select
            value={format}
            onChange={(e) => { setFormat(e.target.value as 'canonical' | 'privatbank'); setPreview(null); }}
          >
            <option value="canonical">Канонічний CSV</option>
            <option value="privatbank">ПриватБанк (Приват24 Бізнес)</option>
          </select>
        </div>
        <div className="field">
          <label>CSV-файл виписки</label>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <button
          className="primary"
          disabled={!file || !orgId || !bankId || previewMut.isPending}
          onClick={() => previewMut.mutate()}
        >
          {previewMut.isPending ? 'Завантаження…' : 'Завантажити та перевірити'}
        </button>
        <span style={{ marginLeft: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          {format === 'canonical'
            ? 'Канонічний CSV: заголовок (date, direction, amount, counterparty_name, counterparty_edrpou, counterparty_iban, purpose, doc_number, doc_date), роздільник кома.'
            : 'ПриватБанк: експорт з Приват24 Бізнес. Роздільник «;», сума зі знаком (+поступлення / −списання), дата DD.MM.YYYY.'}
        </span>
      </div>

      {preview && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.75rem',
            padding: '0.75rem', background: '#f8fafc', borderRadius: 6, marginBottom: '1rem',
          }}>
            <div><strong>Усього: </strong>{preview.rows.length}</div>
            <div><strong style={{ color: '#15803d' }}>Надходжень:</strong> {fmt(preview.total_in)}</div>
            <div><strong style={{ color: '#b91c1c' }}>Списань:</strong> {fmt(preview.total_out)}</div>
            <div>
              <strong>Розпізнано:</strong> {preview.matched_count}/{preview.rows.length}
            </div>
            <div style={{ color: preview.duplicate_count > 0 ? '#b45309' : undefined }}>
              <strong>Можливих дублів:</strong> {preview.duplicate_count}
            </div>
          </div>
          {preview.duplicate_count > 0 && (
            <div style={{
              padding: '0.5rem 0.75rem', background: '#fef3c7', borderRadius: 6,
              marginBottom: '1rem', fontSize: '0.85rem', color: '#92400e',
            }}>
              ⚠ Знайдено {preview.duplicate_count} рядків, які збігаються з вже існуючими
              документами (за датою, сумою та призначенням платежу). Вони автоматично
              позначені як «пропустити» — зніміть позначку, якщо хочете створити документ
              попри дубль.
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem', width: 30 }}>—</th>
                  <th style={{ padding: '0.5rem', width: 30 }}>#</th>
                  <th style={{ padding: '0.5rem', width: 100 }}>Дата</th>
                  <th style={{ padding: '0.5rem', width: 50 }}>↑↓</th>
                  <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>Сума</th>
                  <th style={{ padding: '0.5rem' }}>Контрагент</th>
                  <th style={{ padding: '0.5rem', width: 140 }}>Тип операції</th>
                  <th style={{ padding: '0.5rem' }}>Призначення</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.line_number}
                    style={{
                      borderTop: '1px solid #e2e8f0',
                      opacity: r.skip || r.parse_error ? 0.5 : 1,
                      background: r.parse_error
                        ? '#fef2f2'
                        : r.duplicate_of_id
                          ? '#fef3c7'
                          : undefined,
                    }}
                  >
                    <td style={{ padding: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={!r.skip}
                        disabled={!!r.parse_error}
                        onChange={(e) => updateRow(i, {
                          skip: !e.target.checked,
                          // Если включаем строку-дубль — явно разрешаем создание
                          allow_duplicate: e.target.checked && !!r.duplicate_of_id,
                        })}
                      />
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      {r.line_number}
                      {r.duplicate_of_id && (
                        <div
                          style={{
                            fontSize: '0.7rem',
                            color: '#92400e',
                            fontWeight: 600,
                            marginTop: 2,
                          }}
                          title={`Збігається з документом № ${r.duplicate_of_number}`}
                        >
                          ⚠ дубль
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      {new Date(r.date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                      <span style={{ color: r.direction === 'IN' ? '#15803d' : '#b91c1c' }}>
                        {r.direction === 'IN' ? '↓' : '↑'}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                      {fmt(r.amount)}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      {r.parse_error ? (
                        <span style={{ color: '#b91c1c' }}>⚠ {r.parse_error}</span>
                      ) : (
                        <select
                          value={r.contractor_id ?? ''}
                          onChange={(e) => updateRow(i, { contractor_id: e.target.value || null })}
                          style={{
                            background: r.match_confidence === 'NONE' && !r.contractor_id ? '#fef3c7' : undefined,
                          }}
                        >
                          <option value="">— (без контрагента)</option>
                          {contractorsList.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      )}
                      {r.counterparty_name && (
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.2rem' }}>
                          З виписки: {r.counterparty_name}
                          {r.match_confidence !== 'NONE' && ` (${r.match_confidence})`}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <select
                        value={r.operation_type}
                        onChange={(e) => updateRow(i, { operation_type: e.target.value })}
                      >
                        {(r.direction === 'IN' ? RECEIPT_OPS : PAYMENT_OPS).map((op) => (
                          <option key={op} value={op}>{op}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <input
                        value={r.purpose ?? ''}
                        onChange={(e) => updateRow(i, { purpose: e.target.value })}
                        style={{ fontSize: '0.85rem' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#64748b' }}>
              Буде створено: {rows.filter((r) => !r.skip && !r.parse_error).length} документів
            </span>
            <button
              className="primary"
              disabled={applyMut.isPending}
              onClick={() => applyMut.mutate()}
            >
              {applyMut.isPending ? 'Створення…' : 'Створити документи'}
            </button>
          </div>
        </>
      )}

      {applyResult && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: applyResult.errors.length ? '#fef3c7' : '#dcfce7',
          border: `1px solid ${applyResult.errors.length ? '#fbbf24' : '#86efac'}`,
          borderRadius: 6,
        }}>
          <strong>Готово!</strong>
          <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
            <li>Створено надходжень: {applyResult.created_receipts}</li>
            <li>Створено списань: {applyResult.created_payments}</li>
            <li>Пропущено: {applyResult.skipped}</li>
            {applyResult.skipped_as_duplicate > 0 && (
              <li style={{ color: '#b45309' }}>
                Пропущено як дублі (захист сервера): {applyResult.skipped_as_duplicate}
              </li>
            )}
            {applyResult.errors.length > 0 && (
              <li style={{ color: '#b91c1c' }}>
                Помилок: {applyResult.errors.length}
                <ul>
                  {applyResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </li>
            )}
          </ul>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
            Документи створено в стані «не проведено». Відкрийте «Банк: надходження/списання»,
            перевірте і проведіть.
          </p>
        </div>
      )}
    </div>
  );
}

function statementRowToState(r: StatementRow): RowState {
  return {
    line_number: r.line_number,
    skip: r.parse_error !== null
      || r.duplicate_of_id !== null
      || r.matched_contractor_id === null,
    allow_duplicate: false,
    direction: r.direction,
    date: r.date,
    amount: r.amount,
    contractor_id: r.matched_contractor_id,
    operation_type: r.target_operation_type ?? (r.direction === 'IN' ? 'OTHER' : 'OTHER'),
    purpose: r.purpose,
    document_number: r.doc_number,
    incoming_doc_number: r.doc_number,
    incoming_doc_date: r.doc_date,
    payer_name: r.direction === 'IN' ? r.counterparty_name : null,
    payer_edrpou: r.direction === 'IN' ? r.counterparty_edrpou : null,
    recipient_name: r.direction === 'OUT' ? r.counterparty_name : null,
    recipient_edrpou: r.direction === 'OUT' ? r.counterparty_edrpou : null,
    parse_error: r.parse_error,
    match_confidence: r.match_confidence,
    counterparty_name: r.counterparty_name,
    duplicate_of_id: r.duplicate_of_id,
    duplicate_of_kind: r.duplicate_of_kind,
    duplicate_of_number: r.duplicate_of_number,
  };
}
